import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import { settledGuideToolCount } from '../utils/guided'
import type { AgentMessage } from '../agent/types'
import type { GuideMineItem, GuideNoteView, GuideNoteType, GuideResponseView } from '../types/gotypes'

/**
 * A person's guided conversation (28 Oct 2026): the response, its pinned
 * agenda, the point states and the notes, from GET /guided/:customer/responses/:id
 * (goapi/pkg/server/guided.go). The respondent writes; a Manager reads.
 *
 * The truth is the server's: the agent ticks points and writes notes through
 * its guide tools, so the page refetches when one of those calls ends (the
 * count of settled guide tool calls grows) and when a turn ends. Nothing is
 * mirrored from the chat.
 */

export const guidedBase = (customer: string) => `${API_BASE_URL}/guided/${encodeURIComponent(customer)}`
export const guidedResponseKey = (customer: string, responseId: string) => ['guided', customer, 'response', responseId]

/** A failed read, with its HTTP status: 404 means not yours, or gone. */
export class GuidedRequestError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
  }
}

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status

/** What the person typed in the note dialog. */
export interface GuideNoteDraft {
  point_id: string | null
  type: GuideNoteType
  title: string
  body: string
}

/** A note save: saved, or refused because the note changed meanwhile (409, the latest note attached). */
export type GuideNoteSaveResult =
  | { status: 'saved'; note: GuideNoteView }
  | { status: 'conflict'; note: GuideNoteView }
  | { status: 'error'; error: string }

export interface UseGuidedResponseOptions {
  customer: string
  responseId: string
  /** The chat's messages: a guide tool call ending refetches. Absent on pages with no chat (the agenda). */
  messages?: AgentMessage[]
  /** The chat is answering: the end of a turn refetches. */
  isStreaming?: boolean
}

export default function useGuidedResponse({ customer, responseId, messages, isStreaming = false }: UseGuidedResponseOptions) {
  const queryClient = useQueryClient()
  const key = guidedResponseKey(customer, responseId)
  const base = guidedBase(customer)

  const query = useQuery<GuideResponseView, GuidedRequestError>({
    queryKey: key,
    enabled: Boolean(customer && responseId),
    retry: false,
    queryFn: async () => {
      try {
        return (await axios.get<GuideResponseView>(`${base}/responses/${encodeURIComponent(responseId)}`)).data
      } catch (error) {
        throw new GuidedRequestError(extractErrorMessage(error), statusOf(error))
      }
    },
  })

  const [busy, setBusy] = useState<'' | 'start' | 'finish' | 'note'>('')
  const [actionError, setActionError] = useState('')

  // Trigger: a guide tool call ended, or a turn ended. The refetch is read
  // through a ref so neither effect re-runs when the query object changes.
  const refetchRef = useRef(query.refetch)
  refetchRef.current = query.refetch
  const settled = messages ? settledGuideToolCount(messages) : 0
  useEffect(() => {
    if (settled > 0) void refetchRef.current()
  }, [settled])
  const answeringRef = useRef(false)
  useEffect(() => {
    if (isStreaming) {
      answeringRef.current = true
      return
    }
    if (!answeringRef.current) return
    answeringRef.current = false
    void refetchRef.current()
  }, [isStreaming])

  const setView = (view: GuideResponseView) => queryClient.setQueryData(key, view)
  const patchNotes = (change: (notes: GuideNoteView[]) => GuideNoteView[]) =>
    queryClient.setQueryData<GuideResponseView>(key, (prev) => (prev ? { ...prev, notes: change(prev.notes) } : prev))
  const putNote = (note: GuideNoteView) =>
    patchNotes((notes) => (notes.some((n) => n.id === note.id) ? notes.map((n) => (n.id === note.id ? note : n)) : [...notes, note]))
  const responsePath = `${base}/responses/${encodeURIComponent(responseId)}`

  /** Start (idempotent on the server): pins the agenda and makes the chat. Returns the chat's id, or null. */
  const handleStart = async (): Promise<string | null> => {
    setBusy('start')
    setActionError('')
    try {
      const { data } = await axios.post<{ session_id: string; view: GuideResponseView }>(`${responsePath}/start`)
      setView(data.view)
      return data.session_id
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return null
    } finally {
      setBusy('')
    }
  }

  const handleAddNote = async (draft: GuideNoteDraft): Promise<GuideNoteSaveResult> => {
    setBusy('note')
    try {
      const { data } = await axios.post<GuideNoteView>(`${responsePath}/notes`, draft)
      putNote(data)
      return { status: 'saved', note: data }
    } catch (error) {
      return { status: 'error', error: extractErrorMessage(error) }
    } finally {
      setBusy('')
    }
  }

  /** Save an edit made on `base`. A 409 means the note changed meanwhile: the latest goes back with it. */
  const handleSaveNote = async (baseNote: GuideNoteView, draft: GuideNoteDraft): Promise<GuideNoteSaveResult> => {
    setBusy('note')
    try {
      const { data } = await axios.put<GuideNoteView>(`${responsePath}/notes/${encodeURIComponent(baseNote.id)}`, {
        version: baseNote.version,
        title: draft.title,
        body: draft.body,
        type: draft.type,
        point_id: draft.point_id,
      })
      putNote(data)
      return { status: 'saved', note: data }
    } catch (error) {
      const body = (error as { response?: { data?: { error?: string; note?: GuideNoteView } } })?.response?.data
      if (statusOf(error) === 409 && body?.note) {
        putNote(body.note)
        return { status: 'conflict', note: body.note }
      }
      return { status: 'error', error: extractErrorMessage(error) }
    } finally {
      setBusy('')
    }
  }

  const handleDeleteNote = async (noteId: string): Promise<boolean> => {
    setBusy('note')
    setActionError('')
    try {
      await axios.delete(`${responsePath}/notes/${encodeURIComponent(noteId)}`)
      patchNotes((notes) => notes.filter((n) => n.id !== noteId))
      return true
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return false
    } finally {
      setBusy('')
    }
  }

  /** The person's Finish now. */
  const handleFinish = async (): Promise<boolean> => {
    setBusy('finish')
    setActionError('')
    try {
      const { data } = await axios.post<GuideResponseView>(`${responsePath}/finish`)
      setView(data)
      return true
    } catch (error) {
      setActionError(extractErrorMessage(error))
      return false
    } finally {
      setBusy('')
    }
  }

  return {
    view: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error?.message ?? '',
    notFound: query.error?.status === 404,
    refetch: () => { void query.refetch() },
    busy,
    actionError,
    handleStart,
    handleAddNote,
    handleSaveNote,
    handleDeleteNote,
    handleFinish,
  }
}

/** The caller's own invitations (GET /guided/:customer/mine), newest first as the server sends them. */
export function useGuidedMine(customer: string) {
  const query = useQuery<GuideMineItem[], Error>({
    queryKey: ['guided', customer, 'mine'],
    enabled: Boolean(customer),
    queryFn: async () => {
      try {
        return (await axios.get<{ responses: GuideMineItem[] }>(`${guidedBase(customer)}/mine`)).data.responses ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })
  return { items: query.data ?? [], isLoading: query.isLoading, error: query.error?.message ?? '' }
}
